/* eslint-disable react-hooks/exhaustive-deps */
import FileUpload from "components/FileUpload";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  addDefendantAppeal,
  editDefendantAppeal,
} from "services/appealService";
import { fetchBailRight } from "services/bailRightService";
import { fetchCaseCreation } from "services/caseServices";
import {
  BailRight,
  CaseCreation,
  CaseDefendantLink,
  CountDefendantsList,
  DefendantAppeal,
} from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandler, errorHandlerOnForm } from "utils/helper";

type Props = {
  appeal?: DefendantAppeal;
  // Pre-fill from a previous appeal but create a NEW record (cassation).
  prefillAppeal?: DefendantAppeal;
  // Register a cassation (instead of a normal defendant appeal).
  isCassation?: boolean;
  // Cross-links when the cassation is requested from an appeal.
  originatingProsecutorAppealId?: string;
  parentDefendantAppealId?: string;
  onClose: (refetch: boolean) => void;
};

const DefendantAppealRequestForm: FC<Props> = ({
  appeal,
  prefillAppeal,
  isCassation,
  originatingProsecutorAppealId,
  parentDefendantAppealId,
  onClose,
}) => {
  const { t } = useTranslation();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const [loading, setLoading] = useState(false);
  const [cases, setCases] = useState<CaseCreation[]>([]);
  const [allDefendants, setAllDefendants] = useState<CaseDefendantLink[]>([]);
  const [allCounts, setAllCounts] = useState<CountDefendantsList[]>([]);
  const [bailRights, setBailRights] = useState<BailRight[]>([]);
  const [type, setType] = useState<"case" | "bailright">("case");
  const [prosecutorRegistrationNumber, setProsecutorRegistrationNumber] =
    useState<string>("");
  const [
    prosecutorRegistrationNumberError,
    setProsecutorRegistrationNumberError,
  ] = useState<string>("");

  const [formState, setFormState] = useState<DefendantAppeal>({
    higher_court_type: "",
    higher_court_level: "",
    higher_court_hearing_date: null,
    court_zone: "",
    court_woreda: "",
    court_appeal_number: "",
    court_summons: null,
    opening_date: null,
    first_hearidng_date: null,
    status: "PENDING",
    defendant_ids: [],
  });

  const fetchData = () => {
    setLoading(true);
    fetchBailRight()
      .then((data) => {
        setBailRights(data);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
        setLoading(false);
      });
  };

  useEffect(() => {
    // `appeal` = edit an existing record; `prefillAppeal` = pre-fill from a
    // previous appeal but create a NEW record (defendant cassation).
    const source = appeal || prefillAppeal;
    if (source && cases) {
      setType(source?.case ? "case" : "bailright");
      if (source?.case) {
        const foundCase = cases.find(
          (c) => c.id?.toString() === source?.case?.id?.toString()
        );
        // Prefer the reg number from the source's own case, then the fetched
        // case, then the source object itself (prosecutor appeal carries it).
        setProsecutorRegistrationNumber(
          source?.case?.prosecutor_registration_number ||
            foundCase?.prosecutor_registration_number ||
            (source as any)?.prosecutor_registration_number ||
            ""
        );
        setAllCounts(source?.case?.counts || foundCase?.counts || []);
        setAllDefendants(
          source?.count?.defendants_info ||
            foundCase?.case_defendant_links ||
            []
        );
      }
      setFormState({
        // Only carry the id when editing — prefill always creates a new record.
        id: appeal?.id,
        court_summons: null, // always a fresh upload
        case_id: source?.case ? source?.case?.id : "",
        count_id: source?.case ? source?.count?.id : "",
        bail_right_id: source?.bail_right ? source?.bail_right?.uu_id : "",
        defendant_ids: source?.case
          ? source?.defendants?.map((def) =>
              typeof def === "object" ? def.id : def
            )
          : [],
        // Court fields carried over from the previous appeal (editable).
        higher_court_type: source?.higher_court_type || "",
        higher_court_level: source?.higher_court_level || "",
        higher_court_hearing_date: source?.higher_court_hearing_date || null,
        court_zone: source?.court_zone || "",
        court_woreda: source?.court_woreda || "",
        court_appeal_number: source?.court_appeal_number || "",
        opening_date: source?.opening_date || null,
        first_hearidng_date: source?.first_hearidng_date || null,
        parent_appeal: appeal?.parent_appeal || undefined,
        registrar_number: undefined,
        registrar_date: undefined,
        response_file: undefined,
        assigned_prosecutor: undefined,
        assigned_prosecutor_id: undefined,
        response_summary: undefined,
      } as any);
    }
  }, [appeal, prefillAppeal, cases]);

  const fetchCaseData = () => {
    setLoading(true);
    fetchCaseCreation({ pageSize: 1000 })
      .then((data) => {
        setCases(data.results);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
        setLoading(false);
      });
  };
  useEffect(() => {
    fetchCaseData();
    fetchData();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...(prevState ?? {}),
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formState?.higher_court_type) {
      return "Higher court type is required.";
    }
    if (!formState?.higher_court_level) {
      return "Higher court level is required.";
    }
    if (!formState?.higher_court_hearing_date) {
      return "Higher court hearing date is required.";
    }
    if (!formState?.court_zone) {
      return "Court zone is required.";
    }
    if (!formState?.court_woreda) {
      return "Court woreda is required.";
    }
    if (!formState?.court_appeal_number) {
      return "Court appeal number is required.";
    }
    if (!formState?.opening_date) {
      return "Opening date is required.";
    }
    if (!formState?.first_hearidng_date) {
      return "First hearing date is required.";
    }
    if (
      type === "case" &&
      (!formState?.defendant_ids || formState?.defendant_ids?.length === 0)
    ) {
      return "Defendant is required.";
    }
    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    const bailRight = bailRights?.find(
      (b) => b?.uu_id === formState?.bail_right_id
    );

    setLoading(true);
    // Inherit crime_type from the source appeal (or its case's investigation
    // file) so the role-scoped list (filtered by crime_type) includes it.
    const inheritedCrimeType =
      (prefillAppeal as any)?.crime_type ||
      (appeal as any)?.crime_type ||
      (prefillAppeal?.case as any)?.case_investigation_links?.[0]
        ?.investigation_file_info?.crime_type ||
      (appeal?.case as any)?.case_investigation_links?.[0]
        ?.investigation_file_info?.crime_type ||
      undefined;

    const data: DefendantAppeal = {
      ...formState,
      // Tag as a cassation when requested from an appeal.
      is_casations: isCassation ? true : (formState as any)?.is_casations,
      crime_type: inheritedCrimeType,
      originating_prosecutor_appeal: originatingProsecutorAppealId || undefined,
      parent_appeal: parentDefendantAppealId || undefined,
      admin_unit_id: loginData?.admin_unit + "",
      org_unit_id: loginData?.org_unit + "",
      court_summon: formState?.court_summons
        ? formState.court_summons
        : undefined,
      defendant_ids: formState.defendant_ids || [], // Ensure defendants is always an array
      count_id: type === "case" ? formState.count_id : undefined,
      case_id: type === "case" ? formState.case_id : undefined,
      bail_right_id: type === "bailright" ? formState.bail_right_id : undefined,
      defendent_name_if_bail_right:
        type === "bailright"
          ? bailRight?.suspect_info
            ? bailRight?.suspect_info?.first_name +
              " " +
              bailRight?.suspect_info?.middle_name +
              " " +
              bailRight?.suspect_info?.last_name
            : bailRight?.suspect_fullname
          : "",
    };

    const api = appeal ? editDefendantAppeal : addDefendantAppeal;
    api(data)
      .then(() => {
        showTooltip("Successful!", "success");
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error"
        );
      })
      .finally(() => setLoading(false));
  };
  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative flex w-full max-w-2xl flex-col overflow-y-auto rounded-xl bg-white shadow-xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-gray-100 bg-white px-8 py-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-1.5 rounded-full bg-red-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {isCassation
                  ? t("cassation_registration", {
                      defaultValue: "Cassation Registration",
                    })
                  : t("appeal_registration", {
                      defaultValue: "Appeal Registration",
                    })}
              </h2>
              <p className="mt-0.5 text-sm text-gray-500">
                {isCassation
                  ? t("cassation_form_subtitle", {
                      defaultValue: "Fill in the cassation details below.",
                    })
                  : t("appeal_form_subtitle", {
                      defaultValue: "Fill in the defendant details below.",
                    })}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onClose(false)}
            aria-label="Close"
            className="shrink-0 rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-8 py-6">
          <form className="space-y-5" onSubmit={handleSubmit}>
          <div>
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                {isCassation
                  ? t("select_cassation_type", {
                      defaultValue: "Select Cassation Type",
                    })
                  : t("select_appeal_type", {
                      defaultValue: "Select Appeal Type",
                    })}
              </label>
              <select
                required
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                value={type}
                onChange={(e: any) => {
                  setType(e.target.value);
                  setFormState((prev) => ({
                    ...prev,
                    case_id: "",
                    count_id: "",
                    defendant_ids: [],
                  }));
                }}
              >
                <option value="">-- Select Appeal Type --</option>
                <option value="case">Case</option>
                <option value="bailright">Bail Right</option>
              </select>
            </div>
            {type === "case" && (
              <>
                <div className="flex flex-row gap-4 items-end">
                  <div className="flex flex-1 flex-col mt-4">
                    <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                      Prosecutor Registration Number
                    </label>
                    <input
                      className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                      value={prosecutorRegistrationNumber}
                      onChange={(e) =>
                        setProsecutorRegistrationNumber(e.target.value)
                      }
                      type="text"
                    />
                  </div>
                  <div>
                    <button
                      type="button"
                      className="px-2 py-1 mb-1 border border-gray-100 rounded-lg bg-[#212121] text-white hover:bg-gray-800"
                      onClick={() => {
                        const selectedCase = cases.find(
                          (c) =>
                            c.prosecutor_registration_number?.toString() ===
                            prosecutorRegistrationNumber?.toString()
                        );

                        if (selectedCase) {
                          setProsecutorRegistrationNumberError("");
                          setAllCounts(selectedCase?.counts || []);
                          setAllDefendants(
                            selectedCase?.case_defendant_links || []
                          );
                          setFormState((prev) => ({
                            ...prev,
                            case_id: selectedCase?.id,
                            count_id: "",
                            defendant_ids: [],
                          }));
                        } else {
                          setProsecutorRegistrationNumberError(
                            "Please enter a valid prosecutor registration number."
                          );
                          setAllCounts([]);
                          setAllDefendants([]);
                          setFormState((prev) => ({
                            ...prev,
                            case_id: "",
                            count_id: "",
                            defendant_ids: [],
                          }));
                        }
                      }}
                    >
                      Search
                    </button>
                  </div>
                </div>
                {prosecutorRegistrationNumberError && (
                  <p className="text-red-500 text-sm mt-1">
                    {prosecutorRegistrationNumberError}
                  </p>
                )}
                {/* <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Select Case
                  </label>
                  <select
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="case_id"
                    value={
                      typeof formState?.case_id === "number" ||
                      typeof formState?.case_id === "string"
                        ? formState.case_id
                        : ""
                    }
                    onChange={(e) => {
                      const selectedCase = cases.find(
                        (c) => c.id?.toString() === e.target.value?.toString()
                      );
                      setAllCounts(selectedCase?.counts || []);
                      setAllDefendants(
                        selectedCase?.case_defendant_links || []
                      );
                      setFormState((prev) => ({
                        ...prev,
                        case_id: e.target.value,
                        defendant_ids: [],
                      }));
                    }}
                  >
                    <option value="">-- Select Case --</option>
                    {cases?.map((def: any, idx: number) => (
                      <option key={def.id} value={def?.id}>
                        CR - {def?.crime_refernce_number}
                      </option>
                    ))}
                  </select>
                </div> */}

                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Select Count
                  </label>
                  <select
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="count_id"
                    value={
                      typeof formState?.count_id === "number" ||
                      typeof formState?.count_id === "string"
                        ? formState.count_id
                        : ""
                    }
                    onChange={(e) => {
                      // const selectedCase = cases.find(
                      //   (c) => c.id?.toString() === formState?.case_id?.toString()
                      // );
                      // const selectedCount = (selectedCase?.counts ?? []).find(
                      //   (c) => c.id?.toString() === e.target.value?.toString()
                      // );
                      // setAllDefendants(selectedCount?.defendants_info || []);

                      setFormState((prev) => ({
                        ...prev,
                        count_id: e.target.value,
                        // defendant_ids: [],
                      }));
                    }}
                  >
                    <option value="">-- Select Count --</option>
                    {allCounts?.map((def: any, idx: number) => (
                      <option key={def.id} value={def?.id}>
                        {def?.count_number}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Defendants (already present) */}
                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Select Count Defendant
                  </label>
                  <select
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300 h-32"
                    name="defendant_ids"
                    value={
                      Array.isArray(formState?.defendant_ids)
                        ? formState.defendant_ids.map((file: any) =>
                            typeof file === "object"
                              ? String(file.uu_id ?? file.id)
                              : String(file)
                          )
                        : []
                    }
                    onChange={(e) => {
                      const selectedIds = Array.from(
                        e.target.selectedOptions
                      ).map((option) => option.value);
                      setFormState((prev) => ({
                        ...prev,
                        defendant_ids: selectedIds.map((id) => id),
                      }));
                    }}
                    multiple
                  >
                    {(allDefendants as CaseDefendantLink[])?.map(
                      (def: any, idx: number) => (
                        <option key={def?.id} value={def?.id}>
                          {def?.defendant_info?.first_name}{" "}
                          {def?.defendant_info?.middle_name}{" "}
                          {def?.defendant_info?.last_name}
                          {def?.id}
                        </option>
                      )
                    )}
                  </select>
                </div>
              </>
            )}
            {type === "bailright" && (
              <div className="flex flex-col mt-4">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Select Prosecutor Reg. No.
                </label>
                <select
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="bail_right_id"
                  value={
                    typeof formState?.bail_right_id === "number" ||
                    typeof formState?.bail_right_id === "string"
                      ? formState.bail_right_id
                      : ""
                  }
                  onChange={handleChange}
                >
                  <option value="">-- Select Prosecutor Reg. No. --</option>
                  {bailRights?.map((def: any, idx: number) => (
                    <option key={idx} value={def?.uu_id}>
                      Prosecutor {def?.prosecutor_registration_number}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {/* Higher Court Type */}
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Higher Court Type
              </label>
              <input
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="higher_court_type"
                value={formState?.higher_court_type ?? ""}
                onChange={handleChange}
                type="text"
              />
            </div>
            {/* Higher Court Level */}
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Higher Court Level
              </label>
              <input
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="higher_court_level"
                value={formState?.higher_court_level ?? ""}
                onChange={handleChange}
                type="text"
              />
            </div>
            {/* Higher Court Hearing Date */}
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Higher Court Hearing Date
              </label>
              <input
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="higher_court_hearing_date"
                value={
                  formState?.higher_court_hearing_date
                    ? typeof formState.higher_court_hearing_date === "string"
                      ? formState.higher_court_hearing_date
                      : formState.higher_court_hearing_date
                          .toISOString()
                          .slice(0, 10)
                    : ""
                }
                onChange={handleChange}
                type="date"
              />
            </div>
            {/* Court Zone */}
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Court Zone
              </label>
              <input
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="court_zone"
                value={formState?.court_zone ?? ""}
                onChange={handleChange}
                type="text"
              />
            </div>
            {/* Court Woreda */}
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Court Woreda
              </label>
              <input
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="court_woreda"
                value={formState?.court_woreda ?? ""}
                onChange={handleChange}
                type="text"
              />
            </div>
            {/* Court Appeal Number */}
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Court Appeal Number
              </label>
              <input
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="court_appeal_number"
                value={formState?.court_appeal_number ?? ""}
                onChange={handleChange}
                type="text"
              />
            </div>
            {/* Opening Date */}
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Opening Date
              </label>
              <input
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="opening_date"
                value={
                  formState?.opening_date
                    ? typeof formState.opening_date === "string"
                      ? formState.opening_date
                      : formState.opening_date.toISOString().slice(0, 10)
                    : ""
                }
                onChange={handleChange}
                type="date"
              />
            </div>
            {/* First Hearing Date */}
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                First Hearing Date
              </label>
              <input
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="first_hearidng_date"
                value={
                  formState?.first_hearidng_date
                    ? typeof formState.first_hearidng_date === "string"
                      ? formState.first_hearidng_date
                      : formState.first_hearidng_date.toISOString().slice(0, 10)
                    : ""
                }
                onChange={handleChange}
                type="date"
              />
            </div>
            {/* Court Summons */}
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Court Summons
              </label>
              <FileUpload
                label="Court Summons"
                fileTypes="*"
                info="Upload Summons File"
                required={false}
                onChange={(file) =>
                  setFormState((prevState) => ({
                    ...prevState,
                    court_summons: file,
                  }))
                }
              />
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <button
              type="submit"
              className={`border border-[#212121] bg-[#212121] px-[76px] py-2 
              text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]
                      ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  Loading...
                </div>
              ) : appeal ? (
                t("edit", { defaultValue: "Edit" })
              ) : isCassation ? (
                t("register_cassation", { defaultValue: "Register Cassation" })
              ) : (
                t("register", { defaultValue: "Register" })
              )}
            </button>
          </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DefendantAppealRequestForm;
